import { useEffect, useRef, useState } from 'react';
import { Group, Text } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';

type InlineEditFieldProps = {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  onChange: (value: string) => void;
};

export function InlineEditField({
  label,
  value,
  placeholder,
  multiline = false,
  onChange,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const spanRef = useRef<HTMLSpanElement>(null);

  // Sync external value → DOM (only when not editing to avoid cursor reset)
  useEffect(() => {
    const el = spanRef.current;
    if (el && !editing && el.textContent !== value) {
      el.textContent = value;
    }
  }, [value, editing]);

  // Mount: set initial textContent (no React children on this span)
  useEffect(() => {
    if (spanRef.current) {
      spanRef.current.textContent = value;
    }
    // intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = () => {
    setEditing(true);
    requestAnimationFrame(() => {
      const el = spanRef.current;
      if (!el) return;
      el.focus();
      // Place cursor at end
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    });
  };

  const handleBlur = () => {
    const text = spanRef.current?.textContent ?? '';
    onChange(text);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      spanRef.current?.blur();
    }
    if (e.key === 'Escape') {
      // Revert
      if (spanRef.current) spanRef.current.textContent = value;
      spanRef.current?.blur();
    }
  };

  return (
    <Group align="flex-start" gap="md" wrap="nowrap" className="inline-edit-row">
      <Text size="sm" fw={600} className="inline-edit-label">
        {label}
      </Text>

      {/* Outer box: hover target for pencil */}
      <span
        className={!editing ? 'inline-edit-value' : undefined}
        style={{ flex: 1, minWidth: 0, display: 'block' }}
      >
        {/* contentEditable span — same element in both modes, no layout shift */}
        <span
          ref={spanRef}
          contentEditable={editing || undefined}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          className={`inline-edit-editable${!value ? ' inline-edit-editable--empty' : ''}`}
          onInput={() => onChange(spanRef.current?.textContent ?? '')}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            display: 'inline',
            fontSize: 'var(--mantine-font-size-sm)',
            lineHeight: '1.55',
            color: 'var(--mantine-color-dimmed)',
            caretColor: 'var(--locs-terracotta)',
            outline: 'none',
            wordBreak: 'break-word',
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
          }}
          // No JSX children – textContent is managed via ref to prevent React overwriting the DOM
        />
        {!editing && (
          <span
            className="inline-edit-pencil"
            role="button"
            aria-label={`编辑${label}`}
            title={`编辑${label}`}
            onClick={startEdit}
          >
            <IconPencil size={13} stroke={2} />
          </span>
        )}
      </span>
    </Group>
  );
}
